import { z } from 'zod';

// FUNCTIONS — synchronous logic Dunea calls in the middle of checkout
// (Shopify Functions). Third-party code never runs in Dunea, so a function is
// an HTTPS endpoint on the developer's service, called with a signed request
// and a hard time budget. Its answer is validated and clamped; on a timeout,
// an error or a bad answer Dunea uses the target's fallback and checkout
// carries on. Same cart in, same answer out: responses are cached per cart
// hash for CART_FUNCTION_CACHE_SECONDS so the price a buyer saw is the price
// they pay.
//
//   target                    input                 may return
//   shop.discount             cart                  discounts (never more than the subtotal)
//   shop.shipping-rates       cart + address        shipping options (replace or add to the defaults)
//   shop.payment-methods      cart                  providers to hide, or an order for them
//   shop.cart-validation      cart                  errors that block checkout
//   shop.tax                  cart + address        tax lines (exclusive tax shops only)
//   funnels.offer-price       offer + session       a lower price for this visitor

export const FUNCTION_TARGETS = {
  'shop.discount': { fallback: 'no discount', permission: 'shop:read' },
  'shop.shipping-rates': { fallback: 'the shop default rates', permission: 'orders:read' },
  'shop.payment-methods': { fallback: 'every enabled method', permission: 'shop:read' },
  'shop.cart-validation': { fallback: 'checkout allowed', permission: 'shop:read' },
  'shop.tax': { fallback: 'the shop tax settings', permission: 'orders:read' },
  'funnels.offer-price': { fallback: 'the offer price', permission: 'shop:read' },
} as const;

export type FunctionTarget = keyof typeof FUNCTION_TARGETS;
export const FUNCTION_TARGET_IDS = Object.keys(FUNCTION_TARGETS) as FunctionTarget[];

export const FUNCTION_TIMEOUT_MS = 1_500;
export const CART_FUNCTION_CACHE_SECONDS = 600;
export const FUNCTION_EVENT_HEADER_VALUE = 'function.run';

export const functionDeclarationSchema = z.object({
  target: z.enum(FUNCTION_TARGET_IDS as [FunctionTarget, ...FunctionTarget[]]),
  /** Where Dunea POSTs the signed FunctionRequest. https only. */
  url: z
    .string()
    .url()
    .refine((u) => u.startsWith('https://'), 'must be https'),
  title: z.string().min(2).max(60),
  description: z.string().max(300).optional(),
  /** Lower runs first. Discounts from several functions add up (capped at the subtotal). */
  order: z.number().int().min(0).max(1000).default(100),
});

export type FunctionDeclaration = z.infer<typeof functionDeclarationSchema>;

// ─── wire ───

export interface FunctionCartLine {
  variantId: string;
  productId: string;
  sku: string | null;
  title: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  tags: string[];
  /** Public metafields on the product (namespace.key → value). */
  metafields?: Record<string, unknown>;
}

export interface FunctionCart {
  currency: string;
  lines: FunctionCartLine[];
  subtotal: number;
  discountCode?: string;
  buyer?: { email?: string; country?: string };
  shippingAddress?: { country: string; region?: string; postalCode?: string; city?: string };
  /** Attribution (utm_*) the visitor arrived with. */
  attribution?: Record<string, string>;
}

export interface FunctionRequest<T extends FunctionTarget = FunctionTarget> {
  target: T;
  installId: string;
  appId: string;
  mode: 'test' | 'live';
  cart: FunctionCart;
  /** funnels.offer-price only. */
  offer?: { id: string; amount: number; title: string };
}

export interface DiscountResult {
  discounts: {
    title: string;
    /** 'order' (whole cart), 'shipping', or specific variant ids. */
    target: 'order' | 'shipping' | { variantIds: string[] };
    value: { type: 'percent'; percent: number } | { type: 'amount'; amount: number };
  }[];
}

export interface ShippingRatesResult {
  mode: 'replace' | 'add';
  rates: { id: string; title: string; amount: number; description?: string; minDays?: number; maxDays?: number }[];
}

export interface PaymentMethodsResult {
  hide?: string[];
  order?: string[];
}

export interface CartValidationResult {
  errors: { message: string; variantId?: string }[];
}

export interface TaxResult {
  lines: { title: string; amount: number }[];
}

export interface OfferPriceResult {
  amount: number;
}

export interface FunctionResults {
  'shop.discount': DiscountResult;
  'shop.shipping-rates': ShippingRatesResult;
  'shop.payment-methods': PaymentMethodsResult;
  'shop.cart-validation': CartValidationResult;
  'shop.tax': TaxResult;
  'funnels.offer-price': OfferPriceResult;
}

const id = z.string().min(1).max(100);
const minor = z.number().int().min(0).max(100_000_000);

/** Response validators. Anything that doesn't parse is treated as the fallback. */
export const functionResultSchemas: { [T in FunctionTarget]: z.ZodType<FunctionResults[T]> } = {
  'shop.discount': z.object({
    discounts: z
      .array(
        z.object({
          title: z.string().min(1).max(80),
          target: z.union([z.literal('order'), z.literal('shipping'), z.object({ variantIds: z.array(id).max(100) })]),
          value: z.discriminatedUnion('type', [
            z.object({ type: z.literal('percent'), percent: z.number().gt(0).max(100) }),
            z.object({ type: z.literal('amount'), amount: minor }),
          ]),
        }),
      )
      .max(10),
  }),
  'shop.shipping-rates': z.object({
    mode: z.enum(['replace', 'add']),
    rates: z
      .array(
        z.object({
          id,
          title: z.string().min(1).max(80),
          amount: minor,
          description: z.string().max(200).optional(),
          minDays: z.number().int().min(0).max(365).optional(),
          maxDays: z.number().int().min(0).max(365).optional(),
        }),
      )
      .max(10),
  }),
  'shop.payment-methods': z.object({
    hide: z.array(id).max(20).optional(),
    order: z.array(id).max(20).optional(),
  }),
  'shop.cart-validation': z.object({
    errors: z.array(z.object({ message: z.string().min(1).max(200), variantId: id.optional() })).max(10),
  }),
  'shop.tax': z.object({
    lines: z.array(z.object({ title: z.string().min(1).max(80), amount: minor })).max(10),
  }),
  'funnels.offer-price': z.object({ amount: minor }),
};

/** Parse a function response; null means "use the fallback". */
export function parseFunctionResult<T extends FunctionTarget>(target: T, body: unknown): FunctionResults[T] | null {
  const parsed = functionResultSchemas[target].safeParse(body);

  return parsed.success ? parsed.data : null;
}
